/* Hallmark · component: login-form · genre: modern-minimal · theme: project system
 * (Outfit display · Geist body · indigo accent — preserved from src/index.css)
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass · pre-emit critique: P4 H5 E4 S5 R5 V4
 */

/**
 * LoginPage — pdf-api's own login screen.
 *
 * Wired through `config.auth.components.login` (core-frontend), so it replaces
 * the kernel's built-in LoginForm for THIS app only; dashandwerk, verein and
 * hausverwaltung keep the default.
 *
 * Only the markup lives here. Every piece of auth behaviour — sign-in, social
 * sign-in, the post-login redirect — comes from the kernel's exported hooks.
 * Re-implementing that logic is how the app's ForgotPasswordPage rotted into a
 * stale fork of the kernel's; this file deliberately doesn't repeat that.
 *
 * Redesign vs. the kernel default: the kernel centres everything (logo, a 4xl
 * title, the description, the footer line). Centre-everything is a tell, and a
 * login is a door, not a hero — so the whole column is left-aligned, the title
 * is sized for a door, and the brand statement moved out to AuthBrandPanel.
 */

import { zodResolver } from '@hookform/resolvers/zod'
import {
  Divider,
  GitHubButton,
  GoogleButton,
  MicrosoftButton,
  useAuth,
  useSaaSConfig,
  useSignIn,
  useSitzungsendeHinweis,
  useSocialSignIn,
} from '@xhubio-saas/core-frontend/kernel'
import { Button, cn, Input, Label } from '@xhubio-saas/core-frontend/ui-components'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { AuthAlert, AuthHeading, CONTROL_TRANSITION, FIELD_RING } from './authUi'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const { t } = useTranslation()
  const { signIn } = useSignIn()
  const { isAuthenticated } = useAuth()
  const { signInWithGoogle, signInWithMicrosoft, signInWithGitHub } = useSocialSignIn()
  const config = useSaaSConfig()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  // Der Hinweis nach einem Sitzungsende (`?grund=sitzung-beendet`). Die Weiße
  // Liste des Parameters steht im Kernel-Hook — hier wird nichts geparst.
  const sitzungsendeHinweis = useSitzungsendeHinweis()

  const [showPassword, setShowPassword] = useState(false)
  const [socialPending, setSocialPending] = useState<'google' | 'microsoft' | 'github' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isGoogleEnabled = config.auth?.providers?.includes('google') ?? false
  const isMicrosoftEnabled = config.auth?.providers?.includes('microsoft') ?? false
  const isGitHubEnabled = config.auth?.providers?.includes('github') ?? false
  const hasSocialProviders = isGoogleEnabled || isMicrosoftEnabled || isGitHubEnabled
  const allowRegistration = config.auth?.allowRegistration !== false

  /** Success state: bounced here from the verification mail. */
  const emailVerified = searchParams.get('verified') === 'true'

  // Same precedence the kernel uses: the route the user was bounced from, then
  // the app's configured landing route.
  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    config.layout?.defaultRoute ||
    '/usage'

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) })

  const onSubmit = async (data: LoginFormData) => {
    setError(null)
    const result = await signIn(data.email, data.password, from)
    if (!result.success) setError(result.error || t('auth.loginFailed', 'Login failed'))
  }

  const runSocial = async (
    provider: 'google' | 'microsoft' | 'github',
    fn: () => Promise<unknown>,
    failedKey: string,
    fallback: string
  ) => {
    setSocialPending(provider)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(t(failedKey, fallback))
      console.error(`[LoginPage] ${provider} sign in error:`, err)
    } finally {
      setSocialPending(null)
    }
  }

  const busy = isSubmitting || socialPending !== null
  const fieldRing = FIELD_RING

  // ─────────────────────────────────────────────────────────────────────────────
  // Wer schon angemeldet ist, sieht hier kein Formular, sondern sein Ziel.
  //
  // 🔴 Diese Seite ERSETZT die Anmeldemaske des Kernels (`saas.config.ts`,
  // `auth.components.login`). Die gleichlautende Weiterleitung in
  // `core-frontend/LoginForm.tsx` (Plan DASHANDWERK/08-20/19, Schritt 2)
  // erreicht pdf-api deshalb NICHT — sie muss hier ein zweites Mal stehen.
  // Ohne sie landet der frisch bestätigte Nutzer mit gültigem Cookie auf einer
  // Anmeldemaske, sobald der Bestätigungslink auf `/login?verified=true` zeigt
  // (Schritt 4). Also genau der Zustand, den der Plan behebt.
  //
  // ⚪ Bewusst OHNE `isLoading`-Sperre — Begründung wie im Kernel: eine
  // Anmeldemaske, die auf die Sitzungsabfrage wartet, wird bei hängender
  // Abfrage zur Sackgasse. Der Rückfall bleibt „Formular zeigen".
  // ─────────────────────────────────────────────────────────────────────────────
  if (isAuthenticated) return <Navigate to={from} replace />

  return (
    <div className="flex w-full flex-col gap-8">
      <AuthHeading
        title={t('auth.signIn', 'Sign in')}
        description={t('auth.signInDescription', 'Enter your credentials to access your account')}
      />

      {/* Success — the one state the kernel form also surfaces. */}
      {emailVerified && (
        <div data-testid="login-form-verified-message">
          <AuthAlert tone="success">
            {t('auth.emailVerifiedSuccess', 'Your email has been verified. You can now sign in.')}
          </AuthAlert>
        </div>
      )}

      {/* Info — the session ended (Plan CORE 09-26/01 S2b). Satz und Testid
          sind dieselben wie im Kernel (`login-form-session-ended`); der Text
          kommt aus dem Kernel-Katalog. */}
      {sitzungsendeHinweis && (
        <div data-testid="login-form-session-ended">
          <AuthAlert tone="info">{sitzungsendeHinweis}</AuthAlert>
        </div>
      )}

      {/* Error — one place for both the credential failure and social failures. */}
      {error && <AuthAlert tone="error">{error}</AuthAlert>}

      {hasSocialProviders && (
        <div className="flex flex-col gap-3">
          {isGoogleEnabled && (
            <GoogleButton
              onClick={() =>
                runSocial('google', signInWithGoogle, 'auth.googleFailed', 'Google sign in failed')
              }
              isLoading={socialPending === 'google'}
              disabled={busy}
              t={t}
            />
          )}
          {isMicrosoftEnabled && (
            <MicrosoftButton
              onClick={() =>
                runSocial(
                  'microsoft',
                  signInWithMicrosoft,
                  'auth.microsoftFailed',
                  'Microsoft sign in failed'
                )
              }
              isLoading={socialPending === 'microsoft'}
              disabled={busy}
              t={t}
            />
          )}
          {isGitHubEnabled && (
            <GitHubButton
              onClick={() =>
                runSocial('github', signInWithGitHub, 'auth.githubFailed', 'GitHub sign in failed')
              }
              isLoading={socialPending === 'github'}
              disabled={busy}
              t={t}
            />
          )}
          <Divider t={t} />
        </div>
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-5"
        data-testid="login-form"
        noValidate
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className={cn(fieldRing, errors.email && 'border-destructive')}
            {...register('email')}
          />
          {errors.email && (
            <p id="email-error" className="text-xs text-destructive">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-4">
            <Label htmlFor="password">{t('auth.password', 'Password')}</Label>
            <Link
              to="/forgot-password"
              className={cn(
                'rounded-sm text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline',
                CONTROL_TRANSITION,
                fieldRing
              )}
            >
              {t('auth.forgotPassword', 'Forgot password?')}
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              className={cn('pr-10', fieldRing, errors.password && 'border-destructive')}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              // Hit target stays 40px even though the glyph is 16px.
              className={cn(
                'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-md text-muted-foreground',
                'hover:text-foreground transition-colors duration-[var(--dur-micro)] ease-[var(--ease-out)]',
                fieldRing
              )}
              aria-label={
                showPassword
                  ? t('auth.hidePassword', 'Hide password')
                  : t('auth.showPassword', 'Show password')
              }
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p id="password-error" className="text-xs text-destructive">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Named transition properties, never `transition-all`: the latter would
            animate the focus ring too. `active:` gives the 1px press. */}
        <Button
          type="submit"
          disabled={busy}
          className={cn(
            'h-11 w-full gap-2',
            'transition-[background-color,transform] duration-[var(--dur-micro)] ease-[var(--ease-out)]',
            'active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          {isSubmitting ? (
            <>
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              {t('auth.signIn', 'Sign in')}
            </>
          ) : (
            t('auth.signIn', 'Sign in')
          )}
        </Button>
      </form>

      {allowRegistration && (
        <p className="text-sm text-muted-foreground">
          {t('auth.noAccount', "Don't have an account?")}{' '}
          <Link
            to="/register"
            className={cn(
              'rounded-sm font-medium text-primary underline-offset-4 hover:underline',
              CONTROL_TRANSITION,
              fieldRing
            )}
          >
            {t('auth.signUp', 'Sign up')}
          </Link>
        </p>
      )}
    </div>
  )
}
